import React, { useState, useEffect, Fragment, useMemo, useRef } from 'react';
import { Modal, Form, Space, Button, Typography, Row, Col, Steps, Tour } from 'antd';
import { UserOutlined, AppstoreOutlined, DatabaseOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AgentNameInput from './AgentNameInput/AgentNameInput';
import KnowledgeBaseSection from './KnowledgeBaseSection/KnowledgeBaseSection';
import PlatformSection from './PlatformSection/PlatformSection';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import useMobileFullscreenModalBackHandler from '../../../Hooks/useMobileFullscreenModalBackHandler';
import AdvancedControl from './AdvancedControl/AdvancedControl';
import './AddAgent.css';

const { Step } = Steps;
const { Title } = Typography;

const STEP_VALIDATION_FIELDS = [
    ['name'],
    ['channel_ids'],
    ['knowledgeBaseIds'],
    [] // Advanced controls (no required fields)
];

const KNOWLEDGE_BASE_STEP_INDEX = 2;

const AddAgentModal = ({ onClose, onCreateAgent, mode = 'create', initialData = null, guideOpen = false, onGuideClose }) => {
    const { t } = useTranslation();
    const { loading } = useGlobal();
    const { isDarkMode } = useTheme();
    const [state, setState] = useState('');
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [isMobile, setIsMobile] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [submitDisabled, setSubmitDisabled] = useState(mode !== 'edit');
    const [continueDisabled, setContinueDisabled] = useState(true);
    const [formRevision, setFormRevision] = useState(0);
    const [tourIndex, setTourIndex] = useState(0);
    const [channelCreationActive, setChannelCreationActive] = useState(false);
    const [pagesLoadingForTour, setPagesLoadingForTour] = useState(false);
    const [pagesReadyForTour, setPagesReadyForTour] = useState(false);
    const nameInputRef = useRef(null);
    const instructionsInputRef = useRef(null);
    const platformTypeRef = useRef(null);
    const kbSelectRef = useRef(null);
    const kbCreateFormRef = useRef(null);
    const advAnalysisRef = useRef(null);
    const advConsumptionRef = useRef(null);
    const advProcessingRef = useRef(null);
    const advEscalationRef = useRef(null);
    const submitButtonRef = useRef(null);

    const getDom = (node, selector) => {
        if (node && typeof node.getBoundingClientRect === 'function') return node;
        return selector ? document.querySelector(selector) : null;
    };

    const hasValidationErrors = () => {
        const currentFields = STEP_VALIDATION_FIELDS[currentStep];
        if (!currentFields.length) return false; // For steps with no required fields

        return !!form.getFieldsError(currentFields)
            .filter(({ errors }) => errors.length).length;
    };

    const hasRequiredFields = () => {
        const currentFields = STEP_VALIDATION_FIELDS[currentStep];
        if (!currentFields.length) return true; // For steps with no required fields

        return currentFields.every(field => {
            const value = form.getFieldValue(field);
            switch (field) {
                case 'knowledgeBaseIds':
                    // Check if array has items
                    if (Array.isArray(value) && value.length > 0) {
                        return true;
                    } else if (mode === 'edit' && initialData) {
                        // In edit mode, check if initialData has knowledge base info
                        return !!(initialData.knowledge_base_ids?.length ||
                            initialData.knowledge_bases?.length);
                    }
                    return false;
                case 'channel_ids':
                    return Array.isArray(value) && value.length > 0;
                default:
                    return value !== undefined && value !== null && value !== '';
            }
        });
    };

    const isButtonDisabled = () => {
        if (currentStep === 3) return false; // Advanced step is always enabled

        switch (currentStep) {
            case 0:
                return !form.getFieldValue('name');
            case KNOWLEDGE_BASE_STEP_INDEX:
                const knowledgeBaseIds = form.getFieldValue('knowledgeBaseIds');
                return !Array.isArray(knowledgeBaseIds) || knowledgeBaseIds.length === 0;
            case 1: // Channels step
                const channelIds = form.getFieldValue('channel_ids');
                return !Array.isArray(channelIds) || channelIds.length === 0;
            default:
                return hasValidationErrors() || !hasRequiredFields();
        }
    };

    useEffect(() => {
        const checkIfMobile = () => setIsMobile(window.innerWidth <= 768);
        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    // When this wizard is fullscreen on mobile, consume the system back button to close it first
    useMobileFullscreenModalBackHandler(isMobile, handleCancel);

    // Update button states when step changes or form values change
    useEffect(() => {
        const disabled = isButtonDisabled();
        setContinueDisabled(disabled);
        setSubmitDisabled(disabled);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, formRevision, mode]);

    const handleSubmit = async () => {
        try {
            // Validate all fields regardless of mode
            await form.validateFields();

            const values = form.getFieldsValue(true);

            // Determine the knowledge base IDs array based on form values or initial data
            let knowledgeBaseIds = [];

            // Check for new multi-select array format first: knowledgeBaseIds
            if (Array.isArray(values.knowledgeBaseIds) && values.knowledgeBaseIds.length > 0) {
                knowledgeBaseIds = values.knowledgeBaseIds;
            } else if (typeof values.knowledgeBase === 'object' && values.knowledgeBase?.id) {
                // Legacy format: { id: "123", name: "KB Name" }
                knowledgeBaseIds = [values.knowledgeBase.id];
            } else if (typeof values.knowledgeBase === 'string' && values.knowledgeBase) {
                // Legacy format: "123"
                knowledgeBaseIds = [values.knowledgeBase];
            } else if (mode === 'edit' && initialData?.knowledge_base_ids?.length) {
                // Fallback to initialData if available in edit mode
                knowledgeBaseIds = initialData.knowledge_base_ids;
            } else if (mode === 'edit' && initialData?.knowledge_bases?.length) {
                // Alternative array format in initialData
                knowledgeBaseIds = initialData.knowledge_bases.map(kb => kb.id);
            }

            // Get channel_ids from form values
            const channelIds = values.channel_ids || [];

            // Build submission payload based on new API structure
            const submissionValues = {
                id: mode === 'edit' ? initialData.id : undefined,
                name: values.name,
                knowledge_base_ids: knowledgeBaseIds,
                channel_ids: channelIds,
                process_audio: values.analyzeAudio,
                process_document: values.analyzeDocument,
                process_image: values.analyzeImage,
                process_video: values.analyzeVideo,
                allow_ticket_creation: values.allow_ticket_creation,
                system_instruction: values.system_instruction,
                is_consumption_limit_enabled: values.is_consumption_limit_enabled,
                ...(values.is_consumption_limit_enabled ? {
                    max_coins_consumption: values.max_coins_consumption,
                    consumption_duration: values.consumption_duration,
                } : {}),
                snooze_duration: values.snooze_duration,
                is_processing_delay_enabled: values.is_processing_delay_enabled,
                ...(values.is_processing_delay_enabled ? {
                    processing_delay: values.processing_delay,
                    processing_jitter: values.processing_jitter,
                } : {}),
                // admin_contact_ids is only available in edit mode per API docs
                ...(mode === 'edit' && values.admins ? {
                    admin_contact_ids: values.admins,
                } : {}),
            };

            setState('submitting');
            onCreateAgent(submissionValues, () => setState(''));
        } catch (error) {
            // Validation failed - handled by form UI
        }
    };

    const steps = [
        {
            title: t('agent_info'),
            content: <AgentNameInput nameRef={nameInputRef} instructionsRef={instructionsInputRef} />
        },
        {
            title: t('channels'),
            content: <PlatformSection
                form={form}
                mode={mode}
                id={initialData?.id}
                initialData={initialData}
                onPagesLoadingChange={(loading) => setPagesLoadingForTour(!!loading)}
                onPagesReadyChange={(ready) => setPagesReadyForTour(!!ready)}
                typeRef={platformTypeRef}
                onChannelChange={() => {
                    setFormRevision(r => r + 1);
                }}
                onStartChannelCreation={() => {
                    // When user clicks "Create New Channel", activate channel creation tour steps
                    setChannelCreationActive(true);
                    if (guideOpen && tourIndex === 2) {
                        setTimeout(() => {
                            setCurrentStep(1); // Ensure modal stays on Channels tab
                            setTourIndex(3);
                        }, 400);
                    }
                }}
                onCancelChannelCreation={() => {
                    setChannelCreationActive(false);
                }}
            />
        },
        {
            title: t('knowledge'),
            content: <KnowledgeBaseSection
                initialData={initialData}
                form={form}
                navigate={navigate}
                onUpdate={() => setFormRevision(r => r + 1)}
                onStartCreate={() => {
                    // Keep the tour on the knowledge base step
                    setCurrentStep(KNOWLEDGE_BASE_STEP_INDEX);
                }}
                onCreated={(kb) => {
                    // If tour is on kb-create, bump to next tour step (submit) or proceed within KB steps
                    setFormRevision(r => r + 1);
                }}
                onAssigned={() => {
                    // If the tour is currently on KB select, jump directly to first Advanced step
                    const kbIdx = channelCreationActive ? 6 : 3;
                    if (guideOpen && tourIndex === kbIdx) {
                        setTourIndex(kbIdx + 1);
                    }
                }}
            />
        },
        {
            title: t('advanced'),
            content: <AdvancedControl />
        },
    ];

    const tourIndexToModalStep = useMemo(() => (index) => {
        if (channelCreationActive) {
            if ([0, 1].includes(index)) return 0;
            if ([2, 3, 4, 5].includes(index)) return 1;
            if ([6].includes(index)) return 2;
            return 3;
        } else {
            if ([0, 1].includes(index)) return 0;
            if ([2].includes(index)) return 1;
            if ([3].includes(index)) return 2;
            return 3;
        }
    }, [channelCreationActive]);

    const baseTourSteps = useMemo(() => {
        const commonStart = [
            { title: t('agent_tour_name_title'), description: t('agent_tour_name_desc'), target: () => getDom(nameInputRef.current && nameInputRef.current.input, '[data-tour="agent-name"]') },
            { title: t('agent_tour_instructions_title'), description: t('agent_tour_instructions_desc'), target: () => getDom(instructionsInputRef.current && instructionsInputRef.current.resizableTextArea && instructionsInputRef.current.resizableTextArea.textArea, '[data-tour="agent-instructions"]') },
            { title: t('agent_tour_channels_title', { defaultValue: 'Select Channels' }), description: t('agent_tour_channels_desc', { defaultValue: 'Choose which channels this agent should handle messages from. You can also create a new channel from the dropdown.' }), target: () => getDom(platformTypeRef.current, '[data-tour="platform-type"]') },
        ];

        const channelCreationSteps = channelCreationActive ? [
            { title: t('agent_tour_create_channel_title', { defaultValue: 'Create a Channel' }), description: t('agent_tour_create_channel_desc', { defaultValue: 'Fill in the credential and channel details to create a new channel for your agent.' }), target: () => getDom(null, '[data-tour="channel-credential"]') },
            { title: t('agent_tour_resource_name_title', { defaultValue: 'Select Resource' }), description: t('agent_tour_resource_name_desc', { defaultValue: 'Select the Page, Account, or Number you want to connect to this channel.' }), target: () => getDom(null, '[data-tour="channel-resource"]') },
            { title: t('agent_tour_create_channel_submit_title', { defaultValue: 'Submit Channel' }), description: t('agent_tour_create_channel_submit_desc', { defaultValue: 'Once you have selected the credential, channel type, and page, click here to create the channel.' }), target: () => getDom(null, '[data-tour="channel-create-submit"]') },
        ] : [];

        const commonEnd = [
            { title: t('agent_tour_kb_select_title'), description: t('agent_tour_kb_select_desc'), target: () => getDom(kbSelectRef.current, '[data-tour="kb-select"]') },
            { title: t('agent_tour_adv_analysis_title'), description: t('agent_tour_adv_analysis_desc'), target: () => getDom(advAnalysisRef.current, '[data-tour="adv-analysis"]') },
            { title: t('agent_tour_adv_consumption_title'), description: t('agent_tour_adv_consumption_desc'), target: () => getDom(advConsumptionRef.current, '[data-tour="adv-consumption"]') },
            { title: t('agent_tour_adv_processing_title'), description: t('agent_tour_adv_processing_desc'), target: () => getDom(advProcessingRef.current, '[data-tour="adv-processing"]') },
            { title: t('agent_tour_adv_escalation_title'), description: t('agent_tour_adv_escalation_desc'), target: () => getDom(advEscalationRef.current, '[data-tour="adv-escalation"]') },
            { title: t('agent_tour_submit_title'), description: t('agent_tour_submit_desc'), target: () => getDom(submitButtonRef.current, '[data-tour="wizard-submit"]') },
        ];

        return [...commonStart, ...channelCreationSteps, ...commonEnd];
    }, [t, channelCreationActive]);

    // Compute dynamic indices for index-dependent logic
    const kbStepIndex = channelCreationActive ? 6 : 3;
    const escalationStepIndex = channelCreationActive ? 10 : 7;
    const resourceStepIndex = channelCreationActive ? 4 : -1;
    const submitChannelStepIndex = channelCreationActive ? 5 : -1;

    const isTourNextDisabled = useMemo(() => (index) => {
        switch (index) {
            case 0: // agent name
                return !form.getFieldValue('name');
            default:
                if (index === kbStepIndex) {
                    const kbIds = form.getFieldValue('knowledgeBaseIds');
                    return !Array.isArray(kbIds) || kbIds.length === 0;
                }
                return false;
        }
    }, [form, kbStepIndex]);

    const tourSteps = useMemo(() => (
        baseTourSteps.map((step, idx) => ({
            ...step,
            nextButtonProps: { disabled: isTourNextDisabled(idx) }
        }))
    ), [baseTourSteps, isTourNextDisabled, formRevision]);

    useEffect(() => {
        if (guideOpen) {
            setCurrentStep(tourIndexToModalStep(0));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guideOpen]);

    const next = async () => {
        try {
            if (STEP_VALIDATION_FIELDS[currentStep].length > 0) {
                await form.validateFields(STEP_VALIDATION_FIELDS[currentStep]);
            }
            setCurrentStep(current => Math.min(current + 1, steps.length - 1));
        } catch (error) {
            // Validation failed - handled by form UI
        }
    };

    const handleValuesChange = (changedValues) => {
        // Bump the revision counter to trigger useEffect re-evaluation
        setFormRevision(r => r + 1);

        if (currentStep === 3) {
            setSubmitDisabled(false);
            return;
        }

        if (mode === 'edit') {
            // In edit mode, validate all fields
            form.validateFields()
                .then(() => {
                    setFormRevision(r => r + 1);
                })
                .catch(() => {
                    setFormRevision(r => r + 1);
                });
            return;
        }

        // Create mode: validate step-specific fields for certain field changes
        if ('knowledgeBase' in changedValues || 'type' in changedValues || 'page' in changedValues) {
            form.validateFields(STEP_VALIDATION_FIELDS[currentStep])
                .catch(() => {
                    // Validation errors handled by form UI
                });
        }
    };

    const prev = () => setCurrentStep(current => Math.max(current - 1, 0));

    const isStepValid = (stepIndex) => {
        const fields = STEP_VALIDATION_FIELDS[stepIndex];
        if (!fields.length) return true;
        return fields.every(field => {
            const value = form.getFieldValue(field);
            if (field === 'channel_ids' || field === 'knowledgeBaseIds') {
                return Array.isArray(value) && value.length > 0;
            }
            return value !== undefined && value !== null && value !== '';
        });
    };

    const onStepClick = async (step) => {
        if (mode === 'edit') {
            setCurrentStep(step);
            return;
        }
        // In create mode: allow backward, and allow forward only if all prior steps are valid
        if (step <= currentStep) {
            setCurrentStep(step);
        } else {
            const allPriorValid = Array.from({ length: step }, (_, i) => i)
                .every(i => isStepValid(i));
            if (allPriorValid) {
                setCurrentStep(step);
            }
        }
    };

    return (
        <Modal
            size={isMobile ? 'small' : 'default'}
            title={
                <div style={{ margin: 0 }}>
                    <Title level={4} style={{ margin: '0 0 16px 0' }}>
                        {mode === 'edit' ? t('edit_agent') : t('new_agent')}
                    </Title>
                    <Steps
                        current={currentStep}
                        size="small"
                        onChange={onStepClick}
                        direction="horizontal"
                        responsive={false}
                        className={isMobile ? 'mobile-icon-steps' : ''}
                        style={{
                            marginBottom: 0,
                            position: 'sticky',
                            top: 0,
                            backgroundColor: isDarkMode ? '#1f1f1f' : '#fff',
                            zIndex: 1,
                            padding: '8px 0',
                            paddingBottom: 0,
                        }}
                    >
                        {steps.map((step, index) => (
                            <Step
                                key={index}
                                title={step.title}
                                icon={
                                    index === 0 ? <UserOutlined /> :
                                        index === 1 ? <AppstoreOutlined /> :
                                            index === 2 ? <DatabaseOutlined /> :
                                                <SettingOutlined />
                                }
                                disabled={guideOpen}
                            />
                        ))}
                    </Steps>
                </div>
            }
            open
            onCancel={handleCancel}
            maskClosable={false}
            footer={isMobile ? (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', width: '100%' }}>
                    {currentStep > 0 && (
                        <Button onClick={prev} style={{ maxWidth: '120px' }}>
                            {t('back')}
                        </Button>
                    )}
                    {mode === 'edit' ? (
                        <>
                            <Button
                                type="primary"
                                onClick={handleSubmit}
                                loading={state === 'submitting' && loading}
                                disabled={submitDisabled}
                                style={{
                                    backgroundColor: '#52c41a',
                                    borderColor: '#52c41a',
                                }}
                                data-tour="wizard-submit"
                            >
                                {t('update_agent')}
                            </Button>
                            {currentStep < steps.length - 1 && (
                                <Button
                                    type="primary"
                                    onClick={next}
                                    style={{ maxWidth: '120px' }}
                                >
                                    {t('next')}
                                </Button>
                            )}
                        </>
                    ) : currentStep < steps.length - 1 ? (
                        <Button
                            type="primary"
                            onClick={next}
                            disabled={continueDisabled}
                            style={{ maxWidth: '160px' }}
                        >
                            {t('continue')}
                        </Button>
                    ) : (
                        <Button
                            type="primary"
                            autoFocus
                            onClick={handleSubmit}
                            disabled={submitDisabled}
                            loading={state === 'submitting' && loading}
                            style={{ maxWidth: '160px' }}
                            data-tour="wizard-submit"
                            ref={submitButtonRef}
                        >
                            {t('create_agent')}
                        </Button>
                    )}
                </div>
            ) : (
                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                    <div>
                        {currentStep > 0 && (
                            <Button onClick={prev} style={{ marginInlineEnd: 8 }}>
                                {t('back')}
                            </Button>
                        )}
                    </div>

                    <div>
                        {mode === 'edit' ? (
                            <>
                                <Button
                                    type="primary"
                                    onClick={handleSubmit}
                                    loading={state === 'submitting' && loading}
                                    disabled={submitDisabled}
                                    style={{
                                        backgroundColor: '#52c41a',
                                        borderColor: '#52c41a'
                                    }}
                                    data-tour="wizard-submit"
                                >
                                    {t('update_agent')}
                                </Button>
                                {currentStep < steps.length - 1 && (
                                    <Button
                                        type="primary"
                                        onClick={next}
                                        style={{ marginLeft: 8 }}
                                    >
                                        {t('next')}
                                    </Button>
                                )}
                            </>
                        ) : currentStep < steps.length - 1 ? (
                            <Button
                                type="primary"
                                onClick={next}
                                disabled={continueDisabled}
                            >
                                {t('continue')}
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                autoFocus
                                onClick={handleSubmit}
                                disabled={submitDisabled}
                                loading={state === 'submitting' && loading}
                                data-tour="wizard-submit"
                            >
                                {t('create_agent')}
                            </Button>
                        )}
                    </div>
                </Space>
            )}
            width={isMobile ? "100%" : "900px"}
            style={isMobile ? {
                top: 0,
                margin: 0,
                maxWidth: '100%',
                height: '100vh',
                padding: 0,
            } : {
                maxHeight: '80vh',
                top: '5vh'
            }}
            wrapClassName="custom-modal-wrap"
            styles={{
                header: {
                    padding: '16px 24px',
                    marginBottom: 0,
                    borderBottom: `1px solid ${isDarkMode ? '#434343' : '#f0f0f0'}`,
                    flexShrink: 0,
                    backgroundColor: isDarkMode ? '#1f1f1f' : '#fff',
                },
                body: {
                    height: isMobile ? 'calc(100dvh - 160px)' : '60vh',
                    overflowY: 'auto',
                    padding: isMobile ? '12px 16px 8px 16px' : '16px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    marginTop: 0,
                    flex: 1,
                    minHeight: 0,
                    WebkitOverflowScrolling: 'touch',
                    backgroundColor: isDarkMode ? '#141414' : '#fff',
                },
                footer: {
                    marginTop: 0,
                    paddingTop: 10,
                    paddingBottom: isMobile ? 16 : 10,
                    boxSizing: 'border-box',
                    flexShrink: 0,
                    position: isMobile ? 'sticky' : 'relative',
                    bottom: 0,
                    backgroundColor: isDarkMode ? '#1f1f1f' : '#fff',
                    borderTop: isMobile ? `1px solid ${isDarkMode ? '#434343' : '#f0f0f0'}` : 'none',
                    zIndex: 1000,
                },
                wrapper: {
                    overflowY: isMobile ? 'hidden' : 'auto',
                }
            }}
        >
            <Form
                form={form}
                layout="vertical"
                name="add_agent_form"
                preserve={true}
                initialValues={mode === 'edit' ? {
                    name: initialData.name,
                    system_instruction: initialData.system_instruction,
                    // Multi-select: use knowledgeBaseIds array
                    knowledgeBaseIds: initialData.knowledge_base_ids?.length
                        ? initialData.knowledge_base_ids
                        : initialData.knowledge_bases?.map(kb => kb.id) || [],
                    channel_ids: initialData.channels?.map(ch => ch.id) || [],
                    analyzeImage: initialData.process_image,
                    analyzeVideo: initialData.process_video,
                    analyzeAudio: initialData.process_audio,
                    analyzeDocument: initialData.process_document,
                    allow_ticket_creation: initialData.allow_ticket_creation,
                    is_consumption_limit_enabled: initialData.is_consumption_limit_enabled,
                    max_coins_consumption: initialData.max_coins_consumption,
                    consumption_duration: initialData.consumption_duration,
                    snooze_duration: initialData.snooze_duration,
                    is_processing_delay_enabled: initialData.is_processing_delay_enabled,
                    processing_delay: initialData.processing_delay,
                    processing_jitter: initialData.processing_jitter,
                    admins: initialData.admin_contacts?.map(admin => admin.id) || [],
                } : {
                    name: '',
                    knowledgeBaseIds: [],
                    channel_ids: [],
                    admins: [],
                    system_instruction: 'You are a customer service AI assistant created by Custom Answer, designed to help customers of this platform account.',
                    analyzeImage: true,
                    analyzeVideo: true,
                    analyzeAudio: true,
                    analyzeDocument: true,
                    allow_ticket_creation: true,
                    max_coins_consumption: 60,
                    consumption_duration: 3600,
                    snooze_duration: 600,
                    processing_delay: 15,
                    processing_jitter: 20,
                }}
                onValuesChange={handleValuesChange}
                style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
                onFieldsChange={(changedFields) => {
                    // Bump revision when key fields change to re-evaluate button state
                    if (changedFields.some(field =>
                        ['knowledgeBaseIds', 'channel_ids', 'name'].includes(field.name[0])
                    )) {
                        setFormRevision(r => r + 1);
                    }
                }}
            >
                <div style={{ flex: 1 }}>
                    <Row align="bottom"
                        gutter={16}
                        style={{
                            overflowY: 'auto',
                            padding: '8px 0',
                            marginBottom: 16,
                        }}>
                        {steps.map((step, index) => (
                            <Fragment key={index}>
                                {currentStep === index && (
                                    <Col span={24}>
                                        {step.content}
                                    </Col>
                                )}
                            </Fragment>
                        ))}
                    </Row>
                </div>
            </Form>
            <Tour
                open={guideOpen}
                steps={tourSteps}
                current={tourIndex}
                zIndex={1100}
                rootClassName="add-agent-tour"
                mask={{ style: { zIndex: 1099, pointerEvents: 'none' } }}
                onClose={() => {
                    setTourIndex(0);
                    onGuideClose && onGuideClose();
                }}
                onChange={(next) => {
                    // If navigating to channel creation steps but the form isn't visible, skip them
                    if (channelCreationActive && next >= 3 && next <= 5) {
                        const isFormVisible = !!document.querySelector('[data-tour="channel-credential"]');
                        if (!isFormVisible) {
                            setChannelCreationActive(false);
                            // Going forward → skip to where KB will be (index 3 after reset)
                            // Going backward → stay on channels (index 2)
                            const adjustedNext = next > tourIndex ? 3 : 2;
                            setCurrentStep(tourIndexToModalStep(adjustedNext));
                            setTourIndex(adjustedNext);
                            return;
                        }
                    }

                    setCurrentStep(tourIndexToModalStep(next));
                    setTourIndex(next);

                    // Scroll channel creation fields into view when tour reaches them
                    if (next === resourceStepIndex || next === submitChannelStepIndex) {
                        setTimeout(() => {
                            const selector = next === resourceStepIndex ? '[data-tour="channel-resource"]' : '[data-tour="channel-create-submit"]';
                            const el = document.querySelector(selector);
                            if (el && typeof el.scrollIntoView === 'function') {
                                el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                            }
                        }, 100);
                    }

                    // Ensure escalation setting is visible by scrolling to it when that step is active
                    if (next === escalationStepIndex) {
                        setTimeout(() => {
                            const el = document.querySelector('[data-tour="adv-escalation"]');
                            if (el && typeof el.scrollIntoView === 'function') {
                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                        }, 0);
                    }
                }}
            />
        </Modal>
    );
};

export default AddAgentModal;
